/**
 * Device Fingerprinting for Anonymous Trial Quotas (Phase 2: Persistent Storage)
 *
 * Uses platform-specific persistent storage via @dramadetector/shared:
 * - iOS/Android: expo-secure-store (hardware-backed encryption, Keychain/Keystore)
 * - Web: IndexedDB (survives browser restarts, works when cookies disabled)
 *
 * Benefits:
 * - Better UX: Users don't lose trial progress after app restart
 * - Better security: Hardware-backed encryption on native platforms
 * - Consistent behavior: IndexedDB on web (survives tab close, cookies disabled)
 * - Graceful degradation: Memory-only fallback if storage fails
 *
 * GDPR Compliance Note:
 * - Pseudonymous identifier (not directly linked to personal data)
 * - Persists until app uninstall (native) or browser data clear (web)
 * - No automatic expiration (user controls via app data management)
 * - Used for quota enforcement only, not tracking or profiling
 * - Complies with GDPR Article 6(1)(f) (legitimate interest for abuse prevention)
 *
 * Phase 2 Implementation: 2025-01-24
 * TODO Phase 2.1: Implement 90-day retention cleanup (requires database table + Edge Function)
 * @module device
 */

import {
  getDeviceId as getDeviceIdFromStorage,
} from '@dramadetector/shared/device/storage';
import { Platform } from 'react-native';

export interface DeviceInfo {
  deviceId: string;
  platform: 'ios' | 'android' | 'web';
  createdAt: string;
}

/**
 * Get or create a persistent device identifier
 *
 * Delegates to @dramadetector/shared/device/storage which automatically
 * selects the correct platform-specific implementation via Metro bundler.
 *
 * Performance:
 * - Native (cache hit): 10-20ms (SecureStore read)
 * - Native (cache miss): 30-50ms (SecureStore write on first call)
 * - Web (cache hit): 5-10ms (IndexedDB read)
 * - Web (cache miss): 15-25ms (IndexedDB write on first call)
 *
 * @returns {Promise<string>} Persistent device ID (survives app restarts)
 * @throws {Error} Never throws - falls back to memory-only ID on storage failure
 *
 * @example
 * ```typescript
 * const deviceId = await getDeviceId(); // "ios-1706140800000-abc123xyz"
 * ```
 */
export async function getDeviceId(): Promise<string> {
  // Metro bundler auto-resolves to storage.native.ts or storage.web.ts
  return await getDeviceIdFromStorage();
}

/**
 * Get device info for debugging/analytics
 *
 * Note: Creation timestamp tracking removed in Phase 2 (not essential for quota enforcement)
 * Timestamp can be added back if needed for analytics (requires separate storage)
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const deviceId = await getDeviceId();

  return {
    deviceId,
    platform: Platform.OS as 'ios' | 'android' | 'web',
    createdAt: 'unknown', // Timestamp tracking removed (not essential for Phase 2)
  };
}

/**
 * Reset device ID (for testing only)
 * DO NOT expose this to production users
 *
 * Clears persistent device ID from platform-specific storage (SecureStore/IndexedDB)
 * and memory fallback. Used for E2E test cleanup and quota reset scenarios.
 *
 * @example
 * ```typescript
 * import { resetDeviceId } from './device';
 *
 * await resetDeviceId(); // Clear device ID for testing
 * ```
 */
export async function resetDeviceId(): Promise<void> {
  // In test environment, use require() instead of import() for Jest compatibility
  const storage = process.env.NODE_ENV === 'test'
    ? require('@dramadetector/shared/device/storage')
    : await import('@dramadetector/shared/device/storage');
  await storage.resetDeviceId();
}
